/**
 * Sample report fixture — used only by the SampleReportSection.
 * Lives in its own module so we can swap the demo content without
 * touching component logic or growing the page entrypoint.
 */
export const SAMPLE_REPORT = {
  childName: "Akshitha",
  meta: { dob: "2022-02-04", assessmentDate: "2026-05-16", gender: "Female" },
  mainConcern:
    "Restlessness, frequent unexplained crying/screaming, non-verbal, avoids peers, constant movement/jumping, lack of interest in academic learning (ABCDs).",
  goals: "Improve sitting tolerance, reduce hyperactivity.",
  verifiedBy: "Dr. Dipak Kumar, OT",
  verifiedCreds: "MOT (Pediatrics) · IOTR Reg. No. 07567",
  reassessmentProgress: 12,
  reassessmentTotal: 30,
  focusAreas: [
    "Eye Contact", "Verbal Communication", "Hyperactivity", "Impulsivity",
    "Mood Swings", "Aggression", "Movement Seeking", "Scissors Use",
    "Button/Zip", "Toileting", "Eating Independently", "Self-Care",
    "Dressing", "Imaginative Play", "Problem Solving", "Academic Readiness",
  ],
  domains: [
    { name: "Gross Motor", score: 84, status: "Typical", tone: "ok" },
    { name: "Attention & Self-Regulation", score: 41, status: "Mild to Moderate", tone: "warn" },
    { name: "Sensory Processing", score: 44, status: "Mild to Moderate", tone: "warn" },
    { name: "Fine Motor", score: 38, status: "Mild to Moderate", tone: "warn" },
    { name: "Daily Living & Independence", score: 38, status: "Mild to Moderate", tone: "warn" },
    { name: "Emotion & Behaviour", score: 28, status: "Significant Concern", tone: "alert" },
    { name: "Learning & Play Skills", score: 19, status: "Significant Concern", tone: "alert" },
    { name: "Social & Communication", score: 3, status: "Significant Concern", tone: "alert" },
  ],
  guidance: [
    {
      title: "Gross Motor", score: 84, tone: "ok",
      observation: "Akshitha shows high physical energy, strength, and excellent coordination — movement is her primary way of expressing herself and exploring the world.",
      strategy: "Use her natural movement-seeking as a learning vehicle rather than trying to suppress it.",
      tips: [
        "Build a structured movement schedule (3–4 sessions/day, 10–15 mins each): jumping on a mini-trampoline, bear crawls, frog jumps, crab walks, and obstacle courses around the house.",
        "Incorporate ‘heavy work’ daily — pushing a laundry basket of clothes across the room, wall push-ups (10 reps), carrying small grocery bags, helping to wipe tables.",
        "Use micro-sitting goals (start at 2 minutes) paired with a preferred activity (puzzle, sticker book). Increase by 30 seconds every week.",
        "Outdoor play 30–45 mins daily — running, climbing, swinging are critical regulators.",
      ],
      milestone: "Target: sustained 6-minute sitting tolerance within 4 weeks.",
    },
    {
      title: "Attention & Self-Regulation", score: 41, tone: "warn",
      observation: "Difficulty settling and staying still. Her restlessness is a regulation struggle (her body needs input) — not deliberate misbehavior or defiance.",
      strategy: "Front-load regulating input before any sit-down task. Sandwich seated activities between movement.",
      tips: [
        "5–10 minute heavy-work warm-up before learning (animal walks, push-ups against the wall, jumping jacks).",
        "Use a visible 2-minute sand-timer or visual schedule (‘first sit, then jump’) so she knows when movement is coming.",
        "Designate two clear physical zones at home: a ‘Sitting Spot’ (a small mat or cushion) and a ‘Movement Corner’ (mattress + cushions).",
        "Catch & label moments of self-regulation: ‘You stayed sitting for the whole song — strong body!’",
      ],
      milestone: "Target: independent transition from movement to seated task in under 30 seconds.",
    },
    {
      title: "Sensory Processing", score: 44, tone: "warn",
      observation: "Her nervous system stays ‘switched on’ — she actively seeks intense sensory input (movement, pressure, sound). This is why standard quiet activities feel impossible for her right now.",
      strategy: "Provide planned, predictable sensory input throughout the day so her body isn’t forced to seek it on its own.",
      tips: [
        "Heavy-work routine before any sustained task: pulling/pushing a weighted bag for 10 mins.",
        "Deep pressure: bear hugs, weighted lap pad (1–2 kg), tight squeezes on shoulders before sitting.",
        "Sensory wins box: playdough, kinetic sand, rice play, water bins — rotate to keep novelty.",
        "Reduce auditory clutter during table-time (turn TV off, soft instrumental music only).",
      ],
      milestone: "Target: settle within 3 minutes when offered a deep-pressure tool.",
    },
    {
      title: "Fine Motor", score: 38, tone: "warn",
      observation: "Hand strength and precision need building, but it is hard to practice while her body is restless. Fine motor will progress as regulation improves.",
      strategy: "Bridge movement and hand work — let her body move while her hands focus.",
      tips: [
        "Squeeze playdough while sitting on a wobble cushion or while bouncing on a peanut ball.",
        "‘Busy hands box’ — small containers of rice/lentils, nuts & bolts to twist, clothespins to clip on a cup rim, tongs to pick up pom-poms.",
        "Real-life precision practice: peeling boiled eggs, tearing chapati, threading large beads, opening snack packets.",
        "Pre-writing only after 5 mins of hand warm-up (squeeze ball + finger taps).",
      ],
      milestone: "Target: hold a chunky crayon with tripod grasp for 3 mins.",
    },
    {
      title: "Daily Living & Independence", score: 38, tone: "warn",
      observation: "Transitions and self-care routines feel overwhelming because they are abstract. Visual structure dramatically reduces anxiety.",
      strategy: "Make every routine visible, predictable, and embedded with movement.",
      tips: [
        "Visual routine cards for morning, mealtime, and bedtime (3–4 photo steps each, laminated).",
        "Heavy-work jobs as part of routines — carrying the laundry basket, wiping her own plate, pushing the chair in.",
        "‘First-Then’ board for every transition (‘First brush teeth, then jumps’).",
        "Choice-based independence: offer 2 options at every step (‘red or blue cup?’) to build autonomy.",
      ],
      milestone: "Target: complete 3-step morning routine with visual support, independently.",
    },
    {
      title: "Emotion & Behaviour", score: 28, tone: "alert",
      observation: "Big feelings come out through her body before words — crying, screaming, and movement are her current vocabulary for distress.",
      strategy: "Treat behaviour as communication. Co-regulate first, teach second.",
      tips: [
        "Maintain a ‘sensory diet’ — scheduled movement & input every 60–90 minutes regardless of behaviour.",
        "Name feelings as they happen: ‘Your body feels wiggly — let’s jump 10 times!’ This builds the brain-body link.",
        "Calm-down kit: weighted toy, chewy tube, lavender roller, noise-reducing headphones in a small box she can access.",
        "Avoid talking too much during a meltdown — drop to her level, use 2–3 words max, offer a sensory tool.",
      ],
      milestone: "Target: choose a calming tool independently during 1-of-3 meltdowns by week 4.",
    },
    {
      title: "Social & Communication", score: 3, tone: "alert",
      observation: "Communication is currently expressed through body language and sound. Engagement opens up best inside high-interest, movement-based play.",
      strategy: "Build communication on the back of movement — sing, mirror, narrate, wait.",
      tips: [
        "Imitate every sound, gesture, and movement she makes — this is the foundation of turn-taking.",
        "Use songs with predictable pauses (‘Twinkle… ?’) so she can fill in.",
        "Pair simple gestures with words consistently: ‘more’, ‘open’, ‘go’, ‘stop’.",
        "Visual choice boards (2 photo cards) at snack and play time so she can request without words.",
      ],
      milestone: "Target: spontaneously initiate one shared activity per day.",
    },
    {
      title: "Learning & Play Skills", score: 19, tone: "alert",
      observation: "Sustained academic-style learning is not the right entry point right now. Play and sensory motivators are the bridge.",
      strategy: "Keep learning sessions ultra-short, ultra-motivating, and movement-anchored.",
      tips: [
        "2–3 minute high-interest sessions only: bubbles, water play, light-up toys, fishing magnet games.",
        "Bring letters/numbers into movement — hopscotch with shapes, jumping to call out colours.",
        "Use cause-and-effect toys (pop-up, busy boards) before structured tasks to build attention to objects.",
        "End every learning moment on a high — stop before she loses interest.",
      ],
      milestone: "Target: engage with one cause-and-effect toy for 3 minutes daily.",
    },
  ],
  subSkills: [
    {
      domain: "Attention & Self-Regulation",
      strength: { label: "Attention Switching", score: 75 },
      focus:    { label: "Hyperactivity & Restlessness", score: 25 },
      note: "Akshitha can shift attention well when motivated — this is a real strength. Restlessness is the bottleneck, not attention itself. Prioritize short, daily heavy-work routines targeting hyperactivity before introducing longer seated tasks.",
    },
    {
      domain: "Social & Communication",
      strength: { label: "Peer Interaction", score: 75 },
      focus:    { label: "Social Interest & Engagement", score: 25 },
      note: "She tolerates peers being nearby and watches them — a strong foundation. The next step is shifting from passive observation to shared engagement. Use parallel play with a peer (same toy, side by side) and movement-based group games (‘red light, green light’).",
    },
    {
      domain: "Sensory Processing",
      strength: { label: "Sensory Avoiding", score: 96 },
      focus:    { label: "Sensory Regulation & Attention", score: 10 },
      note: "She has excellent awareness of avoidable input (she pulls back from things that overwhelm her). The growth area is staying regulated for sustained periods. Focus on proactive deep-pressure and proprioceptive input every 60–90 mins to keep her nervous system in a learning-ready state.",
    },
  ],
};
